import { ColumnDef } from "@tanstack/react-table";
import { TrashAltIcon } from "assets/icons";
import { useLanguage } from "hooks";
import { t } from "i18next";
import moment from "moment";
/* eslint-disable react-hooks/exhaustive-deps */
import { useMemo, useState } from "react";
import {
  DateTableCell,
  ItemsTable,
  Status,
  TableItemActionsControl,
  TableItemStatus,
  TableItemStatusControl
} from "ui";

import { useCouponStatusChange } from "../../../apis";
import {
  COUPON_STATUSES_LIST,
  CouponStatus,
  CouponType
} from "../../../constants";
import { CouponItem } from "../../../coupons.types";
import { SharingsCouponsTableProps } from "./SharingsCouponsTable.types";

import styles from "./SharingsCouponsTable.module.scss";

const SharingsCouponsTable = ({
  coupons,
  sorting,
  setSorting,
  isLoading,
  onDeleteCoupon
}: SharingsCouponsTableProps) => {
  const changeCouponStatus = useCouponStatusChange(CouponType.SharingCoupon);

  const { currentLanguage } = useLanguage();

  const [openedItemOptionsIndex, setOpenedItemOptionsIndex] = useState<
    number | null
  >(null);

  const [openedItemStatusesIndex, setOpenedItemStatusesIndex] = useState<
    number | null
  >(null);

  const statusesList: Status[] = COUPON_STATUSES_LIST.map((orStatus) => ({
    ...orStatus,
    onClick: () => {
      if (openedItemStatusesIndex !== null) {
        changeCouponStatus.mutate({
          couponId: coupons[openedItemStatusesIndex].couponId,
          action:
            orStatus.value === CouponStatus.Active ? "activate" : "deactivate"
        });
        setOpenedItemStatusesIndex(null);
      }
    }
  }));

  const columns: Array<ColumnDef<CouponItem, CouponItem[]>> = useMemo(
    () => [
      {
        header: t("Coupon Name"),
        size: 25,
        minSize: 25,
        cell: ({ row }) => (
          <p className={styles.table_boldText}>{coupons[row.index].code}</p>
        )
      },
      {
        header: t("Products to share"),
        size: 15,
        minSize: 15,
        cell: ({ row }) =>
          `${coupons[row.index].requirements.numberOfSharedProducts} ${t("products")}`
      },
      {
        id: "discountAmount",
        header: t("Discount"),
        enableSorting: false,
        size: 15,
        minSize: 15,
        cell: ({ row }) => {
          const coupon = coupons[row.index];
          return coupon.discountPercentage
            ? `${coupon.discountPercentage}%`
            : `${coupon.discountAmount?.toLocaleString("en-US")} FCFA`;
        }
      },
      {
        header: t("From Date"),
        size: 15,
        minSize: 15,
        cell: ({ row }) => <DateTableCell date={coupons[row.index].from} />
      },
      {
        header: t("To Date"),
        size: 15,
        minSize: 15,
        cell: ({ row }) => <DateTableCell date={coupons[row.index].to} />
      },
      {
        header: t("Status"),

        size: 13,
        minSize: 13,
        cell: ({ row }) => {
          const canChangeStatus = moment().isBetween(
            moment(coupons[row.index].from),
            moment(coupons[row.index].to)
          );

          return canChangeStatus ? (
            <TableItemStatusControl
              rowIndex={row.index}
              activeStatusIndex={
                coupons[row.index].status === CouponStatus.Active ? 0 : 1
              }
              openedItemStatusesIndex={openedItemStatusesIndex}
              setOpenedItemStatusesIndex={setOpenedItemStatusesIndex}
              statusesList={statusesList}
            />
          ) : (
            <TableItemStatus
              activeStatusIndex={
                coupons[row.index].status === CouponStatus.Active ? 0 : 1
              }
              statusesList={statusesList}
            />
          );
        }
      },

      {
        accessorKey: "dotsColumn",
        header: () => null,
        size: 2,
        minSize: 2,
        cell: ({ row }) => {
          return (
            <TableItemActionsControl
              actionsList={[
                {
                  label: "Delete",
                  Icon: (
                    <TrashAltIcon fill={"#ff0033"} width={18} height={18} />
                  ),
                  labelColor: "#ff0033",
                  onClick: () => onDeleteCoupon(coupons[row.index].couponId)
                }
              ]}
              rowIndex={row.index}
              setOpenedItemOptionsIndex={setOpenedItemOptionsIndex}
              openedItemOptionsIndex={openedItemOptionsIndex}
            />
          );
        },
        enableSorting: false
      }
    ],
    [coupons, openedItemOptionsIndex, openedItemStatusesIndex, currentLanguage]
  );

  return (
    <>
      <ItemsTable
        columns={columns}
        data={coupons}
        sorting={sorting}
        setSorting={setSorting}
        isLoading={isLoading}
        className={styles.table}
      />
    </>
  );
};

export default SharingsCouponsTable;
